/**
 * Studio hub bookmarks — localStorage-backed saves for radio, collection, playlists, etc.
 */

const STORAGE_KEY = 'studio_bookmarks';

export const STUDIO_BOOKMARK_KINDS = [
  'radio',
  'collection',
  'collection-disk',
  'playlist',
  'playlist-track',
  'playlist-seed',
  'musician',
  'musician-work',
  'player',
  'player-recording',
  'resource',
];

export function bookmarkStorageKey(userId) {
  return userId ? `${STORAGE_KEY}:${userId}` : `${STORAGE_KEY}:guest`;
}

export function bookmarkEntryKey(bookmark) {
  return `${bookmark.kind}:${bookmark.id}`;
}

export function loadStudioBookmarks(userId) {
  try {
    const raw = localStorage.getItem(bookmarkStorageKey(userId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function saveStudioBookmarks(userId, bookmarks) {
  localStorage.setItem(bookmarkStorageKey(userId), JSON.stringify(bookmarks));
}

export function makeBookmark({ kind, id, title, subtitle, tab, payload }) {
  return {
    kind,
    id: String(id),
    title: String(title || '').trim() || 'Untitled',
    subtitle: String(subtitle || '').trim(),
    tab: tab || kind,
    payload: payload || {},
    bookmarkedAt: new Date().toISOString(),
  };
}

export function bookmarkFromRadioStation(station, { radioView = 'regional', regionId = '' } = {}) {
  if (!station?.uuid) return null;
  return makeBookmark({
    kind: 'radio',
    id: station.uuid,
    title: station.name,
    subtitle: [station.country, station.tags?.split(',')[0]].filter(Boolean).join(' · '),
    tab: 'radio',
    payload: {
      radioView,
      regionId,
      station: {
        uuid: station.uuid,
        name: station.name,
        url: station.url,
        countryCode: station.countryCode || '',
        country: station.country || '',
        language: station.language || '',
        tags: station.tags || '',
        iconUrl: station.iconUrl || '',
        hls: Boolean(station.hls),
        source: station.source || '',
      },
    },
  });
}

export function bookmarkFromCollectionTrack(track, disk) {
  if (!track?.id || !disk?.id) return null;
  return makeBookmark({
    kind: 'collection',
    id: `${disk.id}/${track.id}`,
    title: track.title,
    subtitle: `${disk.artist} · ${disk.title}`,
    tab: 'collection',
    payload: {
      diskId: disk.id,
      trackId: track.id,
      name: track.title,
      artist: disk.artist,
      album: disk.title,
      url: track.url || '',
      sourceTab: 'collection',
    },
  });
}

export function bookmarkFromCollectionDisk(disk) {
  if (!disk?.id) return null;
  return makeBookmark({
    kind: 'collection-disk',
    id: disk.id,
    title: disk.title,
    subtitle: disk.artist,
    tab: 'collection',
    payload: { diskId: disk.id },
  });
}

export function bookmarkFromPlaylist(playlist) {
  if (!playlist?.id) return null;
  return makeBookmark({
    kind: 'playlist',
    id: playlist.id,
    title: playlist.name,
    subtitle: playlist.description || `${(playlist.tracks || []).length} tracks`,
    tab: 'playlists',
    payload: { playlistId: playlist.id, regionId: playlist.regionId || '' },
  });
}

export function bookmarkFromPlaylistTrack(track, playlist) {
  if (!track?.id || !playlist?.id) return null;
  return makeBookmark({
    kind: 'playlist-track',
    id: `${playlist.id}/${track.id}`,
    title: track.title,
    subtitle: [track.artist, playlist.name].filter(Boolean).join(' · '),
    tab: 'playlists',
    payload: {
      playlistId: playlist.id,
      trackId: track.id,
      title: track.title,
      artist: track.artist || '',
      genre: track.genre || '',
      duration: track.duration || '',
    },
  });
}

export function bookmarkFromPlaylistSeed(track, region) {
  if (!track?.id || !region?.id) return null;
  return makeBookmark({
    kind: 'playlist-seed',
    id: `${region.id}/${track.id}`,
    title: track.title,
    subtitle: [track.artist || track.subtitle, region.label].filter(Boolean).join(' · '),
    tab: 'playlists',
    payload: {
      regionId: region.id,
      seedTrackId: track.id,
      title: track.title,
      artist: track.artist || track.subtitle || '',
      genre: track.genre || '',
      duration: track.duration || track.meta || '',
    },
  });
}

export function bookmarkFromMusician(musician) {
  if (!musician?.id) return null;
  return makeBookmark({
    kind: 'musician',
    id: musician.id,
    title: musician.nameEn,
    subtitle: musician.country || '',
    tab: 'musicians',
    payload: { musicianId: musician.id },
  });
}

export function bookmarkFromMusicianWork(work, musician, url = '') {
  if (!work?.id || !musician?.id) return null;
  const trackId = `${musician.id}::${work.id}`;
  return makeBookmark({
    kind: 'musician-work',
    id: `${musician.id}/${work.id}`,
    title: work.titleEn || work.title || 'Work',
    subtitle: musician.nameEn,
    tab: 'musicians',
    payload: {
      trackId,
      musicianId: musician.id,
      workId: work.id,
      name: work.titleEn || work.title || 'Work',
      artist: musician.nameEn,
      album: musician.nameEn,
      url: url || '',
      sourceTab: 'musicians',
    },
  });
}

export function bookmarkFromPlayer(player) {
  if (!player?.id) return null;
  return makeBookmark({
    kind: 'player',
    id: player.id,
    title: player.nameEn,
    subtitle: player.country || '',
    tab: 'players',
    payload: { playerId: player.id },
  });
}

export function bookmarkFromPlayerRecording(recording, player, url = '') {
  if (!recording?.id || !player?.id) return null;
  const trackId = `${player.id}::${recording.id}`;
  return makeBookmark({
    kind: 'player-recording',
    id: `${player.id}/${recording.id}`,
    title: recording.titleEn || recording.title || 'Recording',
    subtitle: player.nameEn,
    tab: 'players',
    payload: {
      trackId,
      playerId: player.id,
      recordingId: recording.id,
      name: recording.titleEn || recording.title || 'Recording',
      artist: player.nameEn,
      album: player.nameEn,
      url: url || '',
      sourceTab: 'players',
    },
  });
}

export function bookmarkFromResource(item) {
  if (!item?.id) return null;
  return makeBookmark({
    kind: 'resource',
    id: item.id,
    title: item.title,
    subtitle: [item.format, item.level].filter(Boolean).join(' · '),
    tab: 'resources',
    payload: {
      resourceId: item.id,
      category: item.category,
      region: item.region || 'global',
    },
  });
}

export function bookmarkFromMapItem(item, sectionKind, region) {
  if (!item?.id) return null;
  switch (sectionKind) {
    case 'collection':
      return makeBookmark({
        kind: 'collection-disk',
        id: item.id,
        title: item.title,
        subtitle: item.subtitle || '',
        tab: 'collection',
        payload: { diskId: item.id, regionId: region?.id || '' },
      });
    case 'playlists':
      return bookmarkFromPlaylistSeed(
        {
          id: item.id,
          title: item.title,
          artist: item.subtitle,
          genre: item.meta?.split(' · ')[0] || '',
          duration: item.meta?.split(' · ')[1] || '',
        },
        region,
      );
    case 'musicians':
      return makeBookmark({
        kind: 'musician',
        id: item.id,
        title: item.title,
        subtitle: item.subtitle || '',
        tab: 'musicians',
        payload: { musicianId: item.id, regionId: region?.id || '' },
      });
    case 'players':
      return makeBookmark({
        kind: 'player',
        id: item.id,
        title: item.title,
        subtitle: item.subtitle || '',
        tab: 'players',
        payload: { playerId: item.id, regionId: region?.id || '' },
      });
    default:
      return null;
  }
}
